"use client";

import { getCexLabel } from "@cede/utils";
import type { CampaignWithStats, CampaignSummaryWithStats } from "@/components/features/campaigns/types";
import { CurrencyRewardDisplay } from "@/components/features/currency";
import { ExchangeIcon, TokenIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

export type CampaignType = CampaignWithStats | CampaignSummaryWithStats;

interface NotificationItemProps {
  campaign: CampaignType;
  onDismiss: (campaignId: string) => void;
  onClick: (campaign: CampaignType) => void;
}

export const NotificationItem = ({ campaign, onDismiss, onClick }: NotificationItemProps) => {
  return (
    <div
      className={cn(
        "relative flex items-start gap-3 p-3",
        "rounded-lg",
        "bg-cede-bg-component/50 hover:bg-cede-bg-component",
        "transition-colors duration-200",
        "cursor-pointer",
        "group",
      )}
      onClick={() => onClick(campaign)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(campaign);
        }
      }}
    >
      {/* Icon */}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cede-bg-component/80">
        <TokenIcon symbol={campaign.tokenSymbol || campaign.name} iconUrl={campaign.icon} size="sm" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2 pr-6">
          <div>
            <p className="caption font-medium uppercase tracking-wide text-cede-primary">Private Campaign</p>
            <p className="body2 truncate text-cede-text-primary">{campaign.title || campaign.name}</p>
          </div>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-cede-text-secondary">
          <span className="inline-flex items-center rounded-full border border-cede-border-container bg-cede-bg-component/50 px-2 py-0.5">
            {campaign.tokenSymbol || campaign.name}
          </span>
          {campaign.exchangeIds?.[0] && (
            <span className="inline-flex items-center gap-1 rounded-full border border-cede-border-container bg-cede-bg-component/50 px-2 py-0.5">
              <ExchangeIcon exchange={campaign.exchangeIds[0]} size={12} />
              {getCexLabel(campaign.exchangeIds[0])}
            </span>
          )}
        </div>
        {campaign.currentEpochReward && (
          <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-cede-border-container bg-cede-bg-component/60 px-2.5 py-1 text-xs text-cede-text-secondary">
            <CurrencyRewardDisplay
              usdtValue={campaign.currentEpochReward}
              showIcon
              showLocalCurrency
              layout="inline"
              usdtClassName="font-semibold text-cede-text-primary"
              localClassName="text-cede-text-secondary"
              iconSize={14}
            />
            <span className="text-cede-text-tertiary">epoch reward</span>
          </div>
        )}
      </div>

      {/* Dismiss button */}
      <Button
        variant="transparent"
        size="small"
        className={cn("absolute right-2 top-2", "h-6 w-6 min-w-0 p-0", "opacity-100")}
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(String(campaign.id));
        }}
        aria-label={`Dismiss notification for ${campaign.name}`}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
